import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import {
  Navigate,
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { DevTool } from "@hookform/devtools";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Portal,
  Stack,
  Typography as T,
  Tooltip,
} from "@mui/material";
import DomainDetails from "datasets/pages/settings/components/DomainDetails";
import JSONEditor from "datasets/pages/settings/components/JSONEditor";
import SourceTable from "datasets/pages/settings/components/SourceTable";
import useConfiguratorContext from "datasets/pages/settings/contexts/configurator";
import useActiveConfig from "datasets/pages/settings/hooks/useActiveConfig";
import { BreadcrumbButton, BreadcrumbText } from "files/components/FileBreadcrumbs";
import AbandonmentDialog, {
  ABANDONMENT_DEFAULT,
  AbandonmentDialogProps,
} from "~/components/AbandonmentDialog";
import useErrorToast from "~/contexts/error-toast";
import AccentColor from "~/pages/datasets/types/enums/accent-color";
import { ROUTE_404 } from "~/router/constants";
import rumClient from "~/utils/monitoring";

interface FormModel {
  description: string;
  name: string;
  icon: AccentColor;
  columns: string;
  table: string;
}

const Header = (props: { name?: string }) => {
  const location = useLocation();
  return (
    <Breadcrumbs separator={<KeyboardArrowRight sx={{ fontSize: 22 }} />}>
      <BreadcrumbButton
        component={RouterLink}
        to={{
          pathname: "..",
          search: location.search,
        }}
      >
        <BreadcrumbText>Tables</BreadcrumbText>
      </BreadcrumbButton>
      <BreadcrumbText>{props.name || "New Domain"}</BreadcrumbText>
    </Breadcrumbs>
  );
};

const DomainConfigForm = () => {
  const navigate = useNavigate();
  const { showError } = useErrorToast();
  const { domainId, datasetId } = useParams();
  const { config: currentConfig, loading, addDomain, updateDomain } = useConfiguratorContext();
  const { data: activeConfig } = useActiveConfig();
  const [abandonment, setAbandonment] = useState<AbandonmentDialogProps>(ABANDONMENT_DEFAULT);
  const currentDomain = currentConfig?.content?.tables.find((table) => table.id === domainId);
  const editingExisting = domainId !== "new";
  const form = useForm<FormModel>({
    mode: "onChange",
    defaultValues: {
      columns: JSON.stringify([], null, 2),
      name: "",
      description: "",
      icon: AccentColor.BLUE,
    },
  });

  useEffect(() => {
    if (editingExisting) {
      form.reset({
        name: currentDomain?.name,
        icon: currentDomain?.icon,
        description: currentDomain?.description ?? "",
        columns: JSON.stringify(currentDomain?.columns, null, 2),
        table: currentDomain?.table,
      });
    }
  }, [currentDomain, editingExisting]);

  if (
    editingExisting &&
    (!loading || currentConfig) &&
    !currentConfig?.content?.tables.find(({ id }) => id === domainId)
  ) {
    return <Navigate to={ROUTE_404} replace />;
  }

  const onSubmit = async (data: FormModel) => {
    try {
      const resp = await fetch(
        `/api/cohort-explorer/configurator/configs/validate-domain?dataset_id=${datasetId}`,
        {
          method: "POST",
          body: JSON.stringify({
            // Start from the existing domain so fields this form doesn't edit (id, table_key,
            // disabled, notes, ...) aren't reset to backend defaults. Preserving the id matters
            // because existing cohorts reference domains by it; a new domain gets one from the backend.
            ...(editingExisting ? currentDomain : undefined),
            name: data.name,
            table: data.table,
            description: data.description,
            icon: data.icon,
            columns: JSON.parse(data.columns),
          }),
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const body = await resp.json();

      if (resp.ok) {
        if (!editingExisting) {
          addDomain(body);
        } else {
          updateDomain(domainId!, body);
        }
        navigate("..");
      } else {
        if ((resp.status === 400 || resp.status === 422) && body.message) {
          showError(body.message);
        } else {
          throw Error("There was a problem trying to validate domain");
        }
      }
    } catch (e) {
      console.error(e);
      rumClient?.recordError(e);
      showError("Unable to validate domain");
    }
  };

  const handleOnSubmitAttempt = () => {
    if (activeConfig?.id === currentConfig?.id) {
      form.handleSubmit(onSubmit)();
    } else {
      const clearAbandonment = () => setAbandonment(ABANDONMENT_DEFAULT);
      const continueOldDraft = () => {
        clearAbandonment();
        form.handleSubmit(onSubmit)();
      };

      setAbandonment({
        open: true,
        title: "Outdated Version",
        message:
          "You are editing an outdated version of this table's configuration. If you publish these changes, you will overwrite the latest configuration. Do you want to save these changes?",
        actions: {
          primary: {
            label: "Continue",
            callback: continueOldDraft,
          },
          secondary: {
            label: "Cancel",
            callback: clearAbandonment,
          },
        },
      });
    }
  };

  return (
    <FormProvider {...form}>
      <Box component="form" minHeight="100%">
        <Stack gap={2}>
          <Header name={currentDomain?.name} />
          <DomainDetails />
          <SourceTable />
          <JSONEditor />
          <Box display="flex" gap={2}>
            <Tooltip
              title={
                editingExisting
                  ? "Update configuration with table changes"
                  : "Adds a new table to the configuration"
              }
              arrow
              enterDelay={750}
            >
              <span>
                <LoadingButton
                  disabled={!form.formState.isValid}
                  loading={form.formState.isSubmitting}
                  onClick={() => handleOnSubmitAttempt()}
                  size="medium"
                  sx={{ minWidth: 90 }}
                  variant="contained"
                >
                  Save & Continue
                </LoadingButton>
              </span>
            </Tooltip>
            <Tooltip title="Return to all tables" arrow enterDelay={750}>
              <span>
                <Button
                  component={RouterLink}
                  disabled={form.formState.isSubmitting}
                  sx={{ minWidth: 90 }}
                  to=".."
                  variant="outlined"
                >
                  Cancel
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Stack>
        <AbandonmentDialog {...abandonment} />
        {import.meta.env.DEV && (
          <Portal>
            <DevTool control={form.control} placement="bottom-right" />
          </Portal>
        )}
      </Box>
    </FormProvider>
  );
};

export default DomainConfigForm;
